import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import {
  UpdateProfileRequestSchema,
  type UpdateProfileRequest,
  type User
} from '@bliks-io/contracts';
import { Button, Badge } from '@bliks-io/ui';
import { FormInput } from '@/components/form/rhf';
import { useUpdateProfile } from '@/api/mutations/useProfile';
import { getUserStatusLabel, getUserStatusColorScheme } from '@/lib/user-utils';
import { AvatarUpload } from '@/components/avatar-upload';
import { OrgManagerHint } from '@/components/org-manager-hint';

interface ProfileTabProps {
  user: User;
  onSuccess?: () => void;
}

/**
 * Profile Tab Component
 *
 * Allows users to edit their basic profile information:
 * - First Name (editable)
 * - Last Name (editable)
 * - Email (read-only)
 * - Role (read-only)
 * - Department (read-only)
 * - Status (read-only)
 */
export function ProfileTab({ user, onSuccess }: ProfileTabProps) {
  const { t } = useTranslation();
  const updateProfile = useUpdateProfile();

  const {
    handleSubmit,
    reset,
    control,
    formState: { isDirty, isSubmitting }
  } = useForm<UpdateProfileRequest>({
    resolver: zodResolver(UpdateProfileRequestSchema),
    defaultValues: {
      firstName: user.firstName || '',
      lastName: user.lastName || ''
    }
  });

  useEffect(
    function resetFormOnUserChange() {
      reset({
        firstName: user.firstName || '',
        lastName: user.lastName || ''
      });
    },
    [user, reset]
  );

  const onSubmit = async (data: UpdateProfileRequest) => {
    await updateProfile.mutateAsync(data);
    onSuccess?.();
  };

  return (
    <form noValidate onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Avatar Upload & Name Fields */}
      <div className="pb-6 border-b">
        <div className="grid grid-cols-1 md:grid-cols-[3fr_7fr] gap-6 items-start">
          {/* Avatar Upload */}
          <div className="flex justify-center">
            <AvatarUpload />
          </div>

          {/* Editable Name Fields */}
          <div className="space-y-4 min-w-0">
            <FormInput
              control={control}
              name="firstName"
              label={t('userSettings.profile.firstName')}
              placeholder={t('userSettings.profile.firstName')}
              required
            />

            <FormInput
              control={control}
              name="lastName"
              label={t('userSettings.profile.lastName')}
              placeholder={t('userSettings.profile.lastName')}
              required
            />
          </div>
        </div>
      </div>

      {/* Read-Only Fields */}
      <div className="space-y-4">
        <h3 className="text-sm font-medium text-muted-foreground">
          {t('userSettings.profile.organizationInfo')}
        </h3>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {/* Email - Read Only */}
          <div className="space-y-2">
            <label className="text-sm font-medium">{t('userSettings.profile.email')}</label>
            <div className="flex h-10 w-full rounded-md border border-input bg-muted px-3 py-2 text-sm">
              {user.email}
            </div>
            <OrgManagerHint>{t('userSettings.profile.emailHelper')}</OrgManagerHint>
          </div>

          {/* Status - Read Only */}
          <div className="space-y-2">
            <label className="text-sm font-medium">{t('userSettings.profile.status')}</label>
            <Badge
              colorScheme={getUserStatusColorScheme(user.status)}
              className="h-10 px-3 text-sm flex items-center justify-center w-full"
            >
              {getUserStatusLabel(user.status, t)}
            </Badge>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {/* Department - Read Only */}
          <div className="space-y-2">
            <label className="text-sm font-medium">{t('userSettings.profile.department')}</label>
            <div className="flex h-10 w-full rounded-md border border-input bg-muted px-3 py-2 text-sm">
              {user.department?.name || '-'}
            </div>
            <OrgManagerHint>{t('userSettings.profile.departmentHelper')}</OrgManagerHint>
          </div>

          {/* Stelle (mainRole) - Read Only */}
          <div className="space-y-2">
            <label className="text-sm font-medium">{t('userSettings.profile.role')}</label>
            <div className="flex h-10 w-full rounded-md border border-input bg-muted px-3 py-2 text-sm">
              {user.mainRole?.name || '-'}
            </div>
            <OrgManagerHint>{t('userSettings.profile.roleHelper')}</OrgManagerHint>
          </div>
        </div>
      </div>

      {/* Submit Button */}
      <div className="flex justify-end pt-4">
        <Button type="submit" loading={isSubmitting || updateProfile.isPending} disabled={!isDirty}>
          {t('common.save')}
        </Button>
      </div>
    </form>
  );
}
